/**
 * /finance/purchase-orders — PO Oversight (finance lens)
 * ──────────────────────────────────────────────────────
 * Steve's mid-morning check: what have we committed but not yet billed,
 * where is the vendor concentration, and which POs need finance attention
 * (awaiting bill, ETA unconfirmed, disputed, stale draft).
 *
 * Reads:   VendorPOContext.purchaseOrders (canonical)
 * Writes:  NONE. Route to /finance/bills or /jobs/:id for action.
 *
 * Role gate: financial visibility required (CFO/finance/admin/super_admin).
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldAlert, AlertTriangle, ClockAlert, FileWarning, CheckCircle2 } from "lucide-react";
import { CockpitPage } from "@/components/cockpit/CockpitShell";
import { useVendorPO, type PurchaseOrder, type POStatus } from "@/data/VendorPOContext";
import { useFinancialPermissions } from "@/hooks/useFinancialPermissions";
import { FinanceNavStrip } from "@/components/finance/FinanceTabs";

const MS_DAY = 86_400_000;
const fmt = (n: number) => `$${Math.round(n).toLocaleString()}`;
const fmtCompact = (n: number) =>
  n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(2)}M`
  : n >= 1_000 ? `$${Math.round(n / 1_000)}K`
  : `$${Math.round(n).toLocaleString()}`;

type Lens = "all" | "awaiting-bill" | "eta-unconfirmed" | "disputed" | "stale-draft";

const LENS_LABEL: Record<Lens, string> = {
  "all": "Open commitments",
  "awaiting-bill": "Awaiting bill",
  "eta-unconfirmed": "ETA unconfirmed",
  "disputed": "Disputed",
  "stale-draft": "Stale drafts",
};

const STATUS_TONE: Record<POStatus, string> = {
  Draft: "text-muted-foreground",
  Submitted: "text-warning",
  Confirmed: "text-primary",
  Delivered: "text-success",
};

function isOpenCommitment(po: PurchaseOrder): boolean {
  if (po.finance?.paymentState === "paid") return false;
  if (po.finance?.closeState === "closed") return false;
  return po.status === "Draft" || po.status === "Submitted" || po.status === "Confirmed";
}

function isAwaitingBill(po: PurchaseOrder): boolean {
  return po.status === "Delivered"
    && po.finance?.paymentState !== "paid"
    && po.finance?.closeState !== "closed";
}

function isEtaUnconfirmed(po: PurchaseOrder): boolean {
  return po.status === "Submitted" && !po.etaConfirmed;
}

function isStaleDraft(po: PurchaseOrder): boolean {
  if (po.status !== "Draft") return false;
  const age = Date.now() - new Date(po.createdAt).getTime();
  return age > 7 * MS_DAY;
}

function isDisputed(po: PurchaseOrder): boolean {
  return po.finance?.disputed === true;
}

export default function PurchaseOrdersPage() {
  const { purchaseOrders } = useVendorPO();
  const fin$ = useFinancialPermissions();
  const [lens, setLens] = useState<Lens>("all");

  const buckets = useMemo(() => {
    const open = purchaseOrders.filter(isOpenCommitment);
    const awaitingBill = purchaseOrders.filter(isAwaitingBill);
    const etaUnconfirmed = purchaseOrders.filter(isEtaUnconfirmed);
    const disputed = purchaseOrders.filter(isDisputed);
    const staleDraft = purchaseOrders.filter(isStaleDraft);

    const openByStatus = (s: POStatus) => open.filter((p) => p.status === s);
    const sumCost = (arr: PurchaseOrder[]) => arr.reduce((s, p) => s + p.totalCost, 0);

    return {
      open, awaitingBill, etaUnconfirmed, disputed, staleDraft,
      totals: {
        open: sumCost(open),
        draft: sumCost(openByStatus("Draft")),
        submitted: sumCost(openByStatus("Submitted")),
        confirmed: sumCost(openByStatus("Confirmed")),
        awaitingBill: sumCost(awaitingBill),
        disputed: sumCost(disputed),
      },
      countsByStatus: {
        draft: openByStatus("Draft").length,
        submitted: openByStatus("Submitted").length,
        confirmed: openByStatus("Confirmed").length,
      },
    };
  }, [purchaseOrders]);

  const vendorConcentration = useMemo(() => {
    const m = new Map<string, { vendor: string; openDollars: number; openCount: number }>();
    for (const p of buckets.open) {
      const row = m.get(p.vendorName) ?? { vendor: p.vendorName, openDollars: 0, openCount: 0 };
      row.openDollars += p.totalCost;
      row.openCount += 1;
      m.set(p.vendorName, row);
    }
    return [...m.values()].sort((a, b) => b.openDollars - a.openDollars).slice(0, 5);
  }, [buckets.open]);

  const lensRows = useMemo(() => {
    switch (lens) {
      case "awaiting-bill": return buckets.awaitingBill;
      case "eta-unconfirmed": return buckets.etaUnconfirmed;
      case "disputed": return buckets.disputed;
      case "stale-draft": return buckets.staleDraft;
      default: return [...buckets.open].sort((a, b) => b.totalCost - a.totalCost);
    }
  }, [lens, buckets]);

  if (!fin$.viewFinancialSummary) {
    return (
      <CockpitPage>
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
          <ShieldAlert className="h-6 w-6 text-destructive mb-3" />
          <h1 className="text-xl font-semibold tracking-tight mb-2">Access Restricted</h1>
          <p className="text-sm text-muted-foreground">Purchase order oversight is restricted to finance roles.</p>
        </div>
      </CockpitPage>
    );
  }

  const totalAttention = buckets.awaitingBill.length + buckets.etaUnconfirmed.length + buckets.disputed.length + buckets.staleDraft.length;

  return (
    <CockpitPage>
      <header className="pb-3 border-b border-border/60">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">Purchase Orders · Commitments</h1>
        <p className="text-[12px] text-muted-foreground mt-1">
          {buckets.open.length} open · {fmtCompact(buckets.totals.open)} committed · {totalAttention} need attention
        </p>
      </header>

      <FinanceNavStrip />

      {/* Commitment summary — 4 cells driving lens */}
      <div className="grid grid-cols-2 md:grid-cols-4 border border-border/60 divide-x divide-y md:divide-y-0 divide-border/60 bg-background">
        <button onClick={() => setLens("all")} className={`px-4 py-3 text-left ${lens === "all" ? "bg-primary/5 ring-1 ring-primary/30" : "hover:bg-muted/30"}`}>
          <p className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-medium">Open commitments</p>
          <p className="text-[20px] font-semibold tabular-nums mt-1">{fmtCompact(buckets.totals.open)}</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">{buckets.open.length} POs</p>
        </button>
        <button onClick={() => setLens("awaiting-bill")} className={`px-4 py-3 text-left ${lens === "awaiting-bill" ? "bg-primary/5 ring-1 ring-primary/30" : "hover:bg-muted/30"}`}>
          <p className="text-[10px] uppercase tracking-[0.12em] text-warning font-medium">Awaiting bill</p>
          <p className="text-[20px] font-semibold tabular-nums mt-1 text-warning">{fmtCompact(buckets.totals.awaitingBill)}</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">{buckets.awaitingBill.length} delivered · not billed</p>
        </button>
        <button onClick={() => setLens("eta-unconfirmed")} className={`px-4 py-3 text-left ${lens === "eta-unconfirmed" ? "bg-primary/5 ring-1 ring-primary/30" : "hover:bg-muted/30"}`}>
          <p className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-medium">ETA unconfirmed</p>
          <p className="text-[20px] font-semibold tabular-nums mt-1">{buckets.etaUnconfirmed.length}</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">vendor hasn't acked</p>
        </button>
        <button onClick={() => setLens("disputed")} className={`px-4 py-3 text-left ${lens === "disputed" ? "bg-primary/5 ring-1 ring-primary/30" : "hover:bg-muted/30"}`}>
          <p className="text-[10px] uppercase tracking-[0.12em] text-destructive font-medium">Disputed</p>
          <p className="text-[20px] font-semibold tabular-nums mt-1 text-destructive">{buckets.disputed.length}</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">{fmtCompact(buckets.totals.disputed)} held</p>
        </button>
      </div>

      {/* Two-column: lens table + vendor concentration */}
      <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-3">
        {/* Lens table */}
        <div className="bg-background border border-border/60">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-border/60">
            <p className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-medium">{LENS_LABEL[lens]}</p>
            <span className="text-[11px] text-muted-foreground tabular-nums">{lensRows.length} shown</span>
          </div>
          <div className="grid grid-cols-[110px_1fr_110px_110px_90px] gap-4 px-4 py-2 border-b border-border/60 text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-medium">
            <span>PO #</span>
            <span>Vendor · Job</span>
            <span>Status</span>
            <span className="text-right">Amount</span>
            <span className="text-right">Age</span>
          </div>
          {lensRows.length === 0 ? (
            <div className="px-4 py-10 text-center text-[13px] text-muted-foreground flex flex-col items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-success" />
              {lens === "all" ? "No open POs." : "Nothing to action here."}
            </div>
          ) : (
            lensRows.map((p) => {
              const ageDays = Math.round((Date.now() - new Date(p.createdAt).getTime()) / MS_DAY);
              const flagIcon = isStaleDraft(p) ? <ClockAlert className="h-3 w-3 text-warning" />
                : isDisputed(p) ? <AlertTriangle className="h-3 w-3 text-destructive" />
                : isEtaUnconfirmed(p) ? <FileWarning className="h-3 w-3 text-warning" />
                : null;
              return (
                <div key={p.poId} className="grid grid-cols-[110px_1fr_110px_110px_90px] gap-4 px-4 py-3 border-b border-border/40 last:border-0 hover:bg-muted/20 items-center">
                  <span className="text-[12px] text-muted-foreground font-mono flex items-center gap-1.5">
                    {flagIcon}
                    {p.poNumber}
                  </span>
                  <div className="min-w-0">
                    <p className="text-[13px] font-medium text-foreground truncate">{p.vendorName}</p>
                    <Link to={`/jobs/${p.jobId}`} className="text-[11px] text-muted-foreground hover:text-primary truncate block">
                      {p.jobLabel || p.jobId}
                    </Link>
                  </div>
                  <span className={`text-[11px] ${STATUS_TONE[p.status]}`}>
                    {p.status}
                    {p.etaConfirmed ? " · ack" : ""}
                  </span>
                  <span className="text-right text-[13px] font-semibold tabular-nums">{fmt(p.totalCost)}</span>
                  <span className="text-right text-[11px] text-muted-foreground tabular-nums">{ageDays}d</span>
                </div>
              );
            })
          )}
        </div>

        {/* Vendor concentration */}
        <div className="bg-background border border-border/60">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-border/60">
            <p className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-medium">Top vendors by open $</p>
            <span className="text-[11px] text-muted-foreground tabular-nums">{vendorConcentration.length}</span>
          </div>
          {vendorConcentration.length === 0 ? (
            <p className="px-4 py-8 text-center text-[12px] text-muted-foreground">No open POs.</p>
          ) : (
            vendorConcentration.map((v) => {
              const pct = buckets.totals.open > 0 ? Math.round((v.openDollars / buckets.totals.open) * 100) : 0;
              return (
                <div key={v.vendor} className="px-4 py-3 border-b border-border/40 last:border-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-[13px] font-medium text-foreground truncate">{v.vendor}</p>
                    <span className="text-[12px] font-semibold tabular-nums">{fmtCompact(v.openDollars)}</span>
                  </div>
                  <div className="mt-1.5 h-1 rounded-full bg-muted overflow-hidden">
                    <div className="h-full bg-primary/60" style={{ width: `${pct}%` }} />
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1 tabular-nums">{v.openCount} PO{v.openCount === 1 ? "" : "s"} · {pct}% of open</p>
                </div>
              );
            })
          )}
          <div className="px-4 py-3 border-t border-border/60 text-[11px] text-muted-foreground">
            Concentration risk shows vendors holding the largest share of unbilled commitments. Diversify when a single vendor exceeds 40%.
          </div>
        </div>
      </div>

      {/* Status breakdown */}
      <div className="grid grid-cols-3 border border-border/60 divide-x divide-border/60 bg-background">
        <div className="px-4 py-3">
          <p className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-medium">Draft</p>
          <p className="text-[16px] font-semibold tabular-nums mt-1">{fmtCompact(buckets.totals.draft)}</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">{buckets.countsByStatus.draft} POs</p>
        </div>
        <div className="px-4 py-3">
          <p className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-medium">Submitted</p>
          <p className="text-[16px] font-semibold tabular-nums mt-1">{fmtCompact(buckets.totals.submitted)}</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">{buckets.countsByStatus.submitted} POs</p>
        </div>
        <div className="px-4 py-3">
          <p className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-medium">Confirmed</p>
          <p className="text-[16px] font-semibold tabular-nums mt-1">{fmtCompact(buckets.totals.confirmed)}</p>
          <p className="text-[11px] text-muted-foreground mt-0.5">{buckets.countsByStatus.confirmed} POs · ETA-acked</p>
        </div>
      </div>
    </CockpitPage>
  );
}
